// src/components/pages/OnboardingWizard.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { getWorkspace, updateWorkspace, completeOnboarding } from '../../services/workspaceService';
import { db } from '../../services/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  FiBriefcase, FiUsers, FiGrid, FiCheck, FiArrowRight,
  FiArrowLeft, FiMail, FiPlus, FiX, FiLoader, FiLayers
} from 'react-icons/fi';

const APP_MODULES = [
  { key: 'gestorPro', label: 'Gestor Pro', desc: 'Base de conocimiento y respuestas rapidas', icon: '📋', color: 'from-blue-500 to-indigo-600' },
  { key: 'generador', label: 'Generador', desc: 'Generar mensajes para cursos y eventos', icon: '📝', color: 'from-emerald-500 to-teal-600' },
  { key: 'tareas', label: 'Tareas', desc: 'Gestion de tareas estilo Asana con Kanban', icon: '✅', color: 'from-green-500 to-emerald-600' },
  { key: 'calendario', label: 'Calendario', desc: 'Calendario academico y programacion', icon: '📅', color: 'from-purple-500 to-violet-600' },
  { key: 'agenteIA', label: 'Agente IA', desc: 'Asistente inteligente para tu equipo', icon: '🤖', color: 'from-cyan-500 to-blue-600' },
  { key: 'horasExtra', label: 'Horas Extra', desc: 'Control de asistencia y horas extra', icon: '⏱️', color: 'from-amber-500 to-orange-600' },
  { key: 'ventas', label: 'Ventas', desc: 'Seguimiento de ventas y comisiones', icon: '💰', color: 'from-rose-500 to-pink-600' },
];

const OnboardingWizard = ({ onComplete }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: info empresa, 2: invitar equipo, 3: modulos
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workspace, setWorkspace] = useState(null);

  // Step 2: invitar equipo
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedEmails, setInvitedEmails] = useState([]);

  // Step 3: modulos
  const [selectedModules, setSelectedModules] = useState({
    gestorPro: true, generador: true, tareas: true, calendario: true,
    agenteIA: true, horasExtra: true, ventas: true,
  });

  useEffect(() => {
    const loadWorkspace = async () => {
      if (!user?.workspaceId) return;
      const ws = await getWorkspace(user.workspaceId);
      if (ws) {
        setWorkspace(ws);
        if (ws.modules) setSelectedModules(ws.modules);
      }
      setLoading(false);
    };
    loadWorkspace();
  }, [user]);

  const handleAddInvite = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error('Email invalido');
      return;
    }
    if (invitedEmails.includes(email)) {
      toast.error('Email ya agregado');
      return;
    }
    setInvitedEmails(prev => [...prev, email]);
    setInviteEmail('');
  };

  const handleRemoveInvite = (email) => {
    setInvitedEmails(prev => prev.filter(e => e !== email));
  };

  const handleToggleModule = (key) => {
    setSelectedModules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const workspaceId = user.workspaceId || user.uid;

      // Guardar modulos seleccionados en workspace
      await updateWorkspace(workspaceId, { modules: selectedModules });

      // Guardar appPermissions en el usuario admin
      await updateDoc(doc(db, 'users', user.uid), {
        appPermissions: selectedModules,
      });

      // Marcar onboarding como completado
      await completeOnboarding(workspaceId);

      // Invitaciones: por ahora solo guardar la lista (la funcionalidad de enviar emails se puede agregar despues)
      if (invitedEmails.length > 0) {
        await updateWorkspace(workspaceId, {
          pendingInvitations: invitedEmails,
        });
        toast.success(`${invitedEmails.length} invitacion(es) guardadas`);
      }

      toast.success('Configuracion completada');
      onComplete();
    } catch (error) {
      console.error('Error al completar onboarding:', error);
      toast.error('Error al guardar configuracion');
    }
    setSaving(false);
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await completeOnboarding(user.workspaceId || user.uid);
      toast.success('Puedes configurar esto despues en Ajustes');
      onComplete();
    } catch (error) {
      toast.error('Error al saltar configuracion');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <FiLoader className="animate-spin text-indigo-500" size={32} />
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Empresa', icon: FiBriefcase },
    { num: 2, label: 'Equipo', icon: FiUsers },
    { num: 3, label: 'Modulos', icon: FiGrid },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <FiLayers size={18} />
            </div>
            <span className="text-lg font-bold">Configuracion inicial</span>
          </div>
          <button
            onClick={handleSkip}
            disabled={saving}
            className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
          >
            Saltar y configurar despues
          </button>
        </div>
      </div>

      {/* Step indicators */}
      <div className="px-6 py-6">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-4">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <button
                onClick={() => setStep(s.num)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  step === s.num
                    ? 'bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/30'
                    : step > s.num
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-white/5 text-gray-500'
                }`}
              >
                {step > s.num ? <FiCheck size={14} /> : <s.icon size={14} />}
                {s.label}
              </button>
              {i < steps.length - 1 && <div className="w-12 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-4">
        <div className="max-w-3xl mx-auto">

          {/* STEP 1: Info empresa */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Bienvenido, {user?.displayName || 'Admin'}</h2>
                <p className="text-gray-500">Tu empresa <span className="text-indigo-400 font-medium">{workspace?.name}</span> ha sido aprobada</p>
              </div>

              <div className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-4">
                <h3 className="font-semibold text-sm text-gray-400 uppercase tracking-wider">Datos de tu empresa</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Empresa', value: workspace?.name },
                    { label: 'RUC', value: workspace?.ruc || 'No especificado' },
                    { label: 'Telefono', value: workspace?.phone || 'No especificado' },
                    { label: 'Pais', value: workspace?.country },
                    { label: 'Industria', value: workspace?.industry },
                    { label: 'Tamano', value: workspace?.size },
                  ].map(item => (
                    <div key={item.label} className="bg-white/5 rounded-xl px-4 py-3">
                      <div className="text-xs text-gray-500">{item.label}</div>
                      <div className="text-sm font-medium mt-0.5">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold hover:shadow-lg hover:shadow-indigo-500/25 transition-all flex items-center gap-2"
                >
                  Siguiente <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Invitar equipo */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Invita a tu equipo</h2>
                <p className="text-gray-500">Agrega los emails de las personas que usaran la plataforma</p>
              </div>

              <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
                <div className="flex gap-2 mb-4">
                  <div className="flex-1 relative">
                    <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddInvite())}
                      placeholder="email@empresa.com"
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                    />
                  </div>
                  <button
                    onClick={handleAddInvite}
                    className="px-4 py-3 bg-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-1"
                  >
                    <FiPlus size={16} /> Agregar
                  </button>
                </div>

                {invitedEmails.length > 0 ? (
                  <div className="space-y-2">
                    {invitedEmails.map(email => (
                      <div key={email} className="flex items-center justify-between bg-white/5 rounded-xl px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-xs text-indigo-400 font-bold">
                            {email[0].toUpperCase()}
                          </div>
                          <span className="text-sm">{email}</span>
                        </div>
                        <button onClick={() => handleRemoveInvite(email)} className="text-gray-500 hover:text-red-400 transition-colors">
                          <FiX size={16} />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-gray-600 mt-3">
                      {invitedEmails.length} persona(s) seran invitadas al completar la configuracion
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600">
                    <FiUsers size={32} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Aun no has agregado a nadie</p>
                    <p className="text-xs mt-1">Puedes invitar personas despues desde el panel de admin</p>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="px-5 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all flex items-center gap-2">
                  <FiArrowLeft size={16} /> Atras
                </button>
                <button onClick={() => setStep(3)} className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold hover:shadow-lg hover:shadow-indigo-500/25 transition-all flex items-center gap-2">
                  Siguiente <FiArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Modulos */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Selecciona tus modulos</h2>
                <p className="text-gray-500">Activa los modulos que necesita tu empresa. Puedes cambiar esto despues.</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {APP_MODULES.map(mod => {
                  const active = selectedModules[mod.key];
                  return (
                    <button
                      key={mod.key}
                      onClick={() => handleToggleModule(mod.key)}
                      className={`relative p-4 rounded-2xl border text-left transition-all ${
                        active
                          ? 'bg-white/[0.06] border-indigo-500/30 ring-1 ring-indigo-500/20'
                          : 'bg-white/[0.02] border-white/5 opacity-60 hover:opacity-80'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{mod.icon}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold">{mod.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{mod.desc}</div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          active ? 'bg-indigo-500 border-indigo-500' : 'border-white/20'
                        }`}>
                          {active && <FiCheck size={12} />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(2)} className="px-5 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all flex items-center gap-2">
                  <FiArrowLeft size={16} /> Atras
                </button>
                <button
                  onClick={handleFinish}
                  disabled={saving}
                  className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <><FiLoader className="animate-spin" size={16} /> Guardando...</>
                  ) : (
                    <>Completar configuracion <FiCheck size={16} /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
